import React, { useEffect, useRef } from 'react';
import { create, InstanceProps } from 'react-modal-promise';
import { useLocation, useNavigate } from 'react-router-dom';
import { MenuOpen } from '@mui/icons-material';
import {
  AppBar,
  Box,
  CardMedia,
  Dialog,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  styled,
  Toolbar
} from '@mui/material';

import { EXACARE_LOGOS_S3_BUCKET_URLS } from '@/constants';
import { usePylonChat } from '@/hooks/usePylonChat';
import { MobileNotificationsBell } from '@/pages/AdminHub/Staff/Notifications/components/MobileInboxModal';

import CurrentUser from '../../CurrentUser';
import FacilitySelector from '../../FacilitySelector';
import { useGetSidebarCRMItems } from '../getSidebarCRMItems';
import HelpCenterButton from '../HelpCenterButton';
import { getActiveSidebarPath, StyledNavButton } from '../Sidebar';
import { thinScrollbarSx } from '../sidebarTheme';

const MobileNavigationDialog: React.FC<InstanceProps<unknown>> = ({
  isOpen,
  onResolve
}) => {
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const crmSidebarItems = useGetSidebarCRMItems();
  const activePath = getActiveSidebarPath(crmSidebarItems, pathname);
  const { unreadMessageCount, toggleChat } = usePylonChat();
  const openedAtPathnameRef = useRef(pathname);

  useEffect(() => {
    if (isOpen && pathname !== openedAtPathnameRef.current) {
      onResolve();
    }
  }, [isOpen, onResolve, pathname]);

  const navigateToUrl = (url: string) => {
    onResolve();
    navigate(url);
  };

  return (
    <StyledDialog fullScreen open={isOpen}>
      <AppBar position="sticky" sx={{ flexShrink: 0 }}>
        <Toolbar>
          <IconButton edge="start">
            <MenuOpen onClick={onResolve} />
          </IconButton>
          <CardMedia
            component="img"
            src={EXACARE_LOGOS_S3_BUCKET_URLS.fullBlackAndWhite}
            sx={{ width: 'auto', height: '18px', objectFit: 'contain' }}
          />
          <Box sx={{ ml: 'auto' }}>
            <MobileNotificationsBell />
          </Box>
        </Toolbar>
      </AppBar>
      <Box sx={{ px: 2, pt: 1, flexShrink: 0 }}>
        <FacilitySelector expanded />
      </Box>
      <List
        dense
        component="nav"
        disablePadding
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          '& .MuiListItem-root': { flexShrink: 0 },
          ...thinScrollbarSx,
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'var(--palette-primitive-gray-300)',
            borderRadius: '100px'
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'var(--palette-primitive-gray-300)'
          }
        }}
      >
        {crmSidebarItems.map((item, index) => {
          const { icon, text, path, showOnMobile } = item;

          if (!showOnMobile) {
            return null;
          }

          return (
            <ListItem key={index}>
              <StyledNavButton
                onClick={() => navigateToUrl(path)}
                selected={item.path === activePath}
              >
                <ListItemIcon sx={{ minWidth: 'unset' }}>{icon}</ListItemIcon>
                <ListItemText
                  sx={{ paddingLeft: 2 }}
                  primaryTypographyProps={{
                    whiteSpace: 'nowrap',
                    fontSize: '14px',
                    fontWeight: item.path === activePath ? 700 : 500,
                    color: 'var(--palette-semantic-text-secondary)'
                  }}
                  primary={text}
                />
              </StyledNavButton>
            </ListItem>
          );
        })}
      </List>
      <List
        dense
        disablePadding
        sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}
      >
        <ListItem>
          <HelpCenterButton
            expanded
            unreadMessageCount={unreadMessageCount}
            onToggleChat={toggleChat}
          />
        </ListItem>
        <ListItem sx={{ mb: 1 }}>
          <CurrentUser expanded handleNavigate={onResolve} />
        </ListItem>
      </List>
    </StyledDialog>
  );
};

export const showMobileNavigationDialog = create(MobileNavigationDialog);

const StyledDialog = styled(Dialog)({
  '& .MuiPaper-root': {
    boxShadow: 'unset',
    backgroundColor: 'var(--palette-semantic-background-secondary)',
    padding: 0,
    overflow: 'hidden'
  },
  '& .MuiToolbar-root': {
    height: 64
  },
  '& .MuiCardMedia-root': {
    maxWidth: 140,
    position: 'absolute',
    margin: '0 auto',
    left: 0,
    right: 0
  }
});
